import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { REGIONS, COUNTRY_TABS } from '../data/site';
import { useSite } from '../data/SiteContext';
import NotFoundBlock from './NotFoundBlock';

/**
 * Region and country cards show real photography by borrowing it from the destinations
 * they contain — a country maps 1:1 to a destination, and a region to its first one.
 * Falls back to the generated placeholder while the API is still loading.
 */
const useBrowseImages = () => {
  const { dests } = useSite();
  const norm = (s) => (s || '').toLowerCase().trim();
  const forCountry = (name) => {
    const d = dests.find(x => norm(x.country) === norm(name));
    return d?.img || null;
  };
  const forRegion = (region) => {
    const inRegion = dests.filter(x => norm(x.region) === norm(region.name));
    const withPhoto = inRegion.find(x => x.coverImageFileId) || inRegion[0];
    return withPhoto?.img || region.countries.map(c => forCountry(c.name)).find(Boolean) || null;
  };
  return { forCountry, forRegion };
};

export function RegionsList() {
  const navigate = useNavigate();
  const { forRegion } = useBrowseImages();
  return (
    <div className="wrap" style={{ paddingTop:40, paddingBottom:72 }}>
      <div className="kicker">Destinations</div>
      <h1 className="page-h1">The whole map, one agency.</h1>
      <p className="page-sub">Eleven regions, hand-picked stays and a specialist for each one. Pick a region to explore its countries.</p>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:22 }}>
        {REGIONS.map(r => (
          <div key={r.key} className="lift" onClick={() => navigate('/regions/' + r.key)} style={{ cursor:'pointer', position:'relative', borderRadius:24, overflow:'hidden', minHeight:260, display:'flex', flexDirection:'column', justifyContent:'flex-end', boxShadow:'0 14px 34px rgba(16,90,90,0.16)' }}>
            <div className="cover bgimg" style={{ backgroundImage:`url(${forRegion(r) || r.img})` }}></div>
            <div className="cover" style={{ background:'linear-gradient(180deg,rgba(6,40,45,0) 30%,rgba(6,40,45,0.85) 100%)' }}></div>
            <div style={{ position:'absolute', top:14, right:14, padding:'6px 12px', borderRadius:100, background:'rgba(255,255,255,0.85)', color:'#0c2b2f', fontSize:11.5, fontWeight:800 }}>{r.countries.length} countries</div>
            <div style={{ position:'relative', padding:20, color:'#fff' }}>
              <div className="serif" style={{ fontSize:29, lineHeight:1.05 }}>{r.name}</div>
              <div style={{ fontSize:14, opacity:.9, marginTop:6, lineHeight:1.5 }}>{r.blurb}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RegionCountries() {
  const navigate = useNavigate();
  const { forCountry, forRegion } = useBrowseImages();
  const { regionKey } = useParams();
  const region = REGIONS.find(r => r.key === regionKey);
  if (!region) return <NotFoundBlock title="We don't cover that region yet" message="The link may be out of date. Browse every region we plan trips to instead." backLabel="← All regions" backTo="/regions" />;
  return (
    <div>
      <section style={{ position:'relative', minHeight:340, display:'flex', alignItems:'flex-end', overflow:'hidden' }}>
        <div className="cover bgimg" style={{ backgroundImage:`url(${forRegion(region) || region.img})` }}></div>
        <div className="cover" style={{ background:'linear-gradient(180deg,rgba(6,40,45,0.2),rgba(6,40,45,0.8))' }}></div>
        <div style={{ position:'relative', maxWidth:1240, margin:'0 auto', padding:'0 24px 40px', width:'100%', color:'#fff' }}>
          <span onClick={() => navigate('/regions')} style={{ cursor:'pointer', color:'rgba(255,255,255,0.9)', fontWeight:700, fontSize:14 }}>← All regions</span>
          <h1 className="serif" style={{ fontSize:'clamp(36px,5.5vw,62px)', margin:'12px 0 6px', letterSpacing:'-0.5px' }}>{region.name}</h1>
          <p style={{ fontSize:17, opacity:.92, maxWidth:520, margin:0 }}>{region.blurb}</p>
        </div>
      </section>
      <div className="wrap" style={{ paddingTop:40, paddingBottom:72 }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:22 }}>
          {region.countries.map(c => (
            <div key={c.key} className="card-cool lift3" onClick={() => navigate('/regions/' + region.key + '/' + c.key)} style={{ cursor:'pointer', borderRadius:22, overflow:'hidden', boxShadow:'0 12px 28px rgba(16,90,90,0.12)' }}>
              <div style={{ height:180 }}><div className="bgimg" style={{ backgroundImage:`url(${forCountry(c.name) || c.img})` }}></div></div>
              <div style={{ padding:18 }}>
                <div style={{ fontSize:11.5, fontWeight:800, letterSpacing:'.1em', textTransform:'uppercase', color:'#0e9c86' }}>{c.tag}</div>
                <div className="serif" style={{ fontSize:24, margin:'5px 0 10px' }}>{c.name}</div>
                <div style={{ fontSize:13.5, fontWeight:700, color:'#0e8a9c' }}>Overview · Resorts · Visa · Weather →</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Tab key → the guide entity's flat field prefix.
const GUIDE_FIELD = { 'overview':'overview', 'best-time':'bestTime', 'resorts':'resorts', 'hotels':'hotels', 'excursions':'excursions', 'currency':'currency', 'weather':'weather', 'visa':'visa', 'tips':'tips', 'faq':'faq' };

export function CountryDetail() {
  const navigate = useNavigate();
  const { regionKey, countryKey } = useParams();
  const [ctab, setCtab] = useState('overview');
  const { countryGuides, contact, loading } = useSite();
  const { forCountry, forRegion } = useBrowseImages();
  const region = REGIONS.find(r => r.key === regionKey);
  const country = region?.countries.find(c => c.key === countryKey);
  if (!region || !country) return <NotFoundBlock title="We don't cover that destination yet" message="The link may be out of date. Browse every region we plan trips to instead." backLabel="← All regions" backTo="/regions" />;
  // Only operator-edited guide content renders — no generic filler.
  const guide = countryGuides.find(g => g.key === countryKey && g.isActive);
  const f = GUIDE_FIELD[ctab];
  const gText = guide?.[f + 'Text'];
  const gItems = (guide?.[f + 'Items'] || '').split('\n').map(s => s.trim()).filter(Boolean);
  const tabTitle = COUNTRY_TABS.find(([k]) => k === ctab)?.[1] || '';
  const tabData = { title: tabTitle, text: gText || '', items: gItems };
  const hasContent = Boolean(gText || gItems.length);
  const guideSources = (guide?.sources || '').split('\n').map(s => s.trim()).filter(s => /^https?:\/\//.test(s));
  return (
    <div>
      <section style={{ position:'relative', minHeight:380, display:'flex', alignItems:'flex-end', overflow:'hidden' }}>
        <div className="cover bgimg" style={{ backgroundImage:`url(${forCountry(country.name) || country.img})` }}></div>
        <div className="cover" style={{ background:'linear-gradient(180deg,rgba(6,40,45,0.15),rgba(6,40,45,0.82))' }}></div>
        <div style={{ position:'relative', maxWidth:1240, margin:'0 auto', padding:'0 24px 40px', width:'100%', color:'#fff' }}>
          <span onClick={() => navigate('/regions/' + region.key)} style={{ cursor:'pointer', color:'rgba(255,255,255,0.9)', fontWeight:700, fontSize:14 }}>← {region.name}</span>
          <div style={{ display:'block' }}>
            <div style={{ display:'inline-block', margin:'12px 0 8px', padding:'6px 14px', borderRadius:100, background:'rgba(255,255,255,0.2)', border:'1px solid rgba(255,255,255,0.4)', fontSize:12.5, fontWeight:700 }}>{country.tag}</div>
          </div>
          <h1 className="serif" style={{ fontSize:'clamp(38px,6vw,66px)', margin:0, letterSpacing:'-0.5px' }}>{country.name}</h1>
        </div>
      </section>
      <div style={{ position:'sticky', top:88, zIndex:20, background:'#f1fbf8', borderBottom:'1px solid #d8efe8' }}>
        <div style={{ maxWidth:1240, margin:'0 auto', padding:'14px 24px', display:'flex', gap:8, overflowX:'auto' }}>
          {COUNTRY_TABS.map(([k, label]) => (
            <div key={k} className={'ctab' + (ctab === k ? ' active' : '')} onClick={() => setCtab(k)}>{label}</div>
          ))}
        </div>
      </div>
      <div style={{ maxWidth:1240, margin:'0 auto', padding:'36px 24px 72px', display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:36, alignItems:'start' }}>
        <div>
          <h2 className="serif" style={{ fontSize:34, margin:'0 0 12px' }}>{tabData.title}</h2>
          {!hasContent && (
            <p style={{ fontSize:16.5, lineHeight:1.7, color:'#5a726f', margin:'0 0 22px' }}>
              {loading ? 'Loading…' : `We're still writing this section — ask us directly and a specialist will answer about ${country.name} today.`}
            </p>
          )}
          {tabData.text && <p style={{ fontSize:16.5, lineHeight:1.7, color:'#31474a', margin:'0 0 22px' }}>{tabData.text}</p>}
          <div style={{ display:'grid', gap:10 }}>
            {tabData.items.map(i => (
              <div key={i} className="card-cool" style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'15px 17px', borderRadius:14, fontSize:14.5, fontWeight:600, lineHeight:1.5 }}>
                <span style={{ flexShrink:0, width:22, height:22, borderRadius:'50%', background:'linear-gradient(135deg,#26c6a3,#0e8a9c)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:12 }}>✓</span>{i}
              </div>
            ))}
          </div>
          {guideSources.length > 0 && (
            <div style={{ marginTop:22, fontSize:12.5, color:'#8aa19d', lineHeight:1.7 }}>
              Information sources:{' '}
              {guideSources.map((u, ix) => (
                <a key={u} href={u} target="_blank" rel="noreferrer" style={{ color:'#5a8a94' }}>{new URL(u).hostname.replace('www.','')}{ix < guideSources.length - 1 ? ' · ' : ''}</a>
              ))}
            </div>
          )}
        </div>
        <div className="card-cool" style={{ padding:26, borderRadius:24, boxShadow:'0 16px 40px rgba(16,90,90,0.14)' }}>
          <div className="serif" style={{ fontSize:24, marginBottom:8 }}>Plan {country.name} with us</div>
          <p style={{ fontSize:14.5, lineHeight:1.6, color:'#5a726f', margin:'0 0 18px' }}>Tell us your dates and we'll send a tailored itinerary with flights, stays and excursions within 24 hours.</p>
          <a href={contact.waLink} target="_blank" rel="noreferrer" className="btn btn-green" style={{ display:'block', padding:15, boxShadow:'0 10px 22px rgba(18,153,76,0.35)' }}>Ask on WhatsApp</a>
          <a href={contact.telLink} style={{ display:'block', textAlign:'center', marginTop:10, padding:13, borderRadius:14, fontWeight:700, color:'#0e8a9c', background:'#eafaf3' }}>Call {contact.phone}</a>
          <div style={{ marginTop:20, height:170, borderRadius:16, overflow:'hidden' }}><div className="bgimg" style={{ backgroundImage:`url(${forRegion(region) || region.img})` }}></div></div>
        </div>
      </div>
    </div>
  );
}
